import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc';
import "rxjs/add/operator/timeout";
import "rxjs/add/operator/take";

import { IUserContext } from '../store/state/IUserContext';
import * as fromStore from '../store/reducers';
import * as rootServices from '../../core/services';
import * as actions from '../store/actions';
import { AuthenticationConfig } from '../auth.config';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';

export const LoginRedirectUrlKey = 'login_redirect_url';
@Injectable()
export class SecurityServerService {
  userContext: IUserContext;
  documentLoaded: boolean = false;
  readonly loginRedirectUrlKey = LoginRedirectUrlKey;

  public authenticationConfig: AuthenticationConfig;

  constructor(private store: Store<IUserContext>,
    private dialog: rootServices.EnhancedDialogService,
    private oauthService: OAuthService,
    private location: Location,
    appConfigurationService: AppConfigurationService) {
    store.select(fromStore.getUserContext).subscribe(uc =>
      this.userContext = uc
    );
    if (appConfigurationService.configuration.securityServer.disabled) {
      this.store.dispatch(new actions.UserIdentityUpdatedAction({ claims: {}, authToken: '' }));
    } else {
      this.oauthService.sessionChecksEnabled = true;
      this.authenticationConfig = new AuthenticationConfig(appConfigurationService);
      this.oauthService.configure(this.authenticationConfig);
      this.oauthService.tokenValidationHandler = new JwksValidationHandler();
      this.oauthService.setupAutomaticSilentRefresh();
      this.oauthService.setStorage({
        ignoreNextNonce: false,
        getItem(s): string {
          if (s === 'nonce' && this.ignoreNextNonce) {
            this.ignoreNextNonce = false;
            return undefined;
          }
          return sessionStorage.getItem(s);
        },
        setItem(k, v) {
          sessionStorage.setItem(k, v);
        },
        removeItem(k) {
          sessionStorage.removeItem(k);
        }
      } as any)
      this.oauthService.loadDiscoveryDocumentAndTryLogin()
        .then(() => {
          if (this.oauthService.hasValidIdToken()) {
            this.store.dispatch(new actions.UserIdentityUpdatedAction({ claims: this.oauthService.getIdentityClaims(), authToken: this.oauthService.getIdToken() }));
            return true;
          }
          return false;
        });
      this.oauthService.events.subscribe(c => {
        console.log(c);
        switch (c.type) {
          case 'token_received':
            var service = this.oauthService as any;
            var parts = service.urlHelper.getHashFragmentParams();
            var accessToken = parts['access_token'];
            var idToken = parts['id_token'];
            service._storage.ignoreNextNonce = true;
            this.oauthService.processIdToken(idToken, accessToken)
              .then((result) => {
                result.idTokenClaims['preferred_username'] = result.idTokenClaims['cognito:username'];
                service.storeIdToken(result);
                if (!this.userContext.isAuthenticated && this.oauthService.getIdentityClaims()) {
                  var claims: any = this.oauthService.getIdentityClaims();
                  this.store.dispatch(new actions.UserIdentityUpdatedAction({ claims, authToken: this.oauthService.getIdToken() }));
                }
              });
            break;
          case 'silently_refreshed':
          case 'token_refreshed':
            this.store.dispatch(new actions.UserIdentityUpdatedAction({ claims: this.oauthService.getIdentityClaims(), authToken: this.oauthService.getIdToken() }));
            break;
          case 'discovery_document_loaded':
            this.documentLoaded = true;
            break;
          case 'silent_refresh_error':
          case 'token_refresh_error':
          case 'session_error':
          case 'session_terminated':
          case 'token_error':
          case 'token_validation_error':
            window.location.reload();
            break;
          case 'logout':
            window.location.href = this.authenticationConfig.logoutUrl;
            break;
          case 'jwks_load_error':
          case 'discovery_document_load_error':
          case 'discovery_document_validation_error':
          case 'user_profile_load_error':
            throw new Error("Error contacting security server. Please contact your administrator if you continue to see this error.");
          default:
        }
      });
    }
  }

  private tryLogin(resolve: (value?: boolean) => void, doLogin: boolean) {
    this.oauthService.tryLogin()
      .then(() => {
        if (!this.oauthService.hasValidIdToken()) {
          if (doLogin) {
            sessionStorage.setItem(this.loginRedirectUrlKey, window.location.pathname);
            localStorage.setItem(this.loginRedirectUrlKey, window.location.pathname);
            this.oauthService.initImplicitFlow();
          }
          resolve(false);
        } else {
          resolve(true);
        }
      });
  }

  public checkAuthentication(doLogin: boolean = true): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (!this.userContext.isAuthenticated) {
        if (this.documentLoaded) {
          this.tryLogin(resolve, doLogin);
        } else {
          this.oauthService.events
            .timeout(3000)
            .filter(e => e.type === 'discovery_document_loaded')
            .take(1)
            .subscribe(c => {
              this.tryLogin(resolve, doLogin);
            },
              error => {
                resolve(false);
              });
        }
      } else {
        resolve(true);
      }
    });
  }

  public logout() {
    this.oauthService.logOut();
    localStorage.removeItem(this.loginRedirectUrlKey);
  }

  public changePassword() {
    window.location.assign(this.authenticationConfig.changePasswordUri + '?redirectUri=' + window.location);
  }
}



// WEBPACK FOOTER //
// ./src/app/security/services/SecurityServerService.ts